<template>
  <div class="row justify-content-center">
    <div class="col-md-6 col-lg-5">
      <div class="forgot-password-card">
        <!-- Before Submit: Password Reset Request Form -->
        <div v-if="!submitted" class="form-section">
          <h1 class="page-title">{{ $t('auth.forgotPassword.title') }}</h1>

          <p class="description">
            {{ $t('auth.forgotPassword.description1') }}<br>
            {{ $t('auth.forgotPassword.description2') }}
          </p>

          <form @submit.prevent="submit" novalidate>
            <div class="form-group">
              <label for="email" class="form-label">{{ $t('auth.forgotPassword.email') }}</label>
              <input
                type="email"
                id="email"
                class="form-control"
                :class="{ 'is-invalid': errors.email }"
                v-model="email"
                placeholder="example@town.higashikawa.lg.jp"
                :disabled="loading"
              >
              <div v-if="errors.email" class="invalid-feedback">
                {{ Array.isArray(errors.email) ? errors.email[0] : errors.email }}
              </div>
            </div>

            <button
              type="submit"
              class="btn btn-primary btn-submit"
              :disabled="!email || loading"
            >
              <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
              {{ $t('auth.forgotPassword.submit') }}
            </button>

            <button
              type="button"
              class="btn btn-secondary btn-back"
              @click="goToLogin"
              :disabled="loading"
            >
              {{ $t('auth.forgotPassword.backToLogin') }}
            </button>
          </form>
        </div>

        <!-- After Submit: Confirmation Message -->
        <div v-else class="confirmation-section">
          <h1 class="page-title">{{ $t('auth.forgotPassword.confirmTitle') }}</h1>

          <p class="description">
            {{ $t('auth.forgotPassword.confirmDescription1') }}<strong>{{ submittedEmail }}</strong><br>
            {{ $t('auth.forgotPassword.confirmDescription2') }}
          </p>

          <button
            type="button"
            class="btn btn-secondary btn-back"
            @click="goToLogin"
          >
            {{ $t('auth.forgotPassword.backToLogin') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { forgotPassword } from '../../../api/user/AuthApi';
import useFlashMessage from '../../../composables/useFlashMessage';
import { colors } from '../../../../consts/colors.js';

const router = useRouter();
const { t } = useI18n();
const { errorMessage } = useFlashMessage();

const email = ref('');
const loading = ref(false);
const errors = ref({});
const submitted = ref(false);
const submittedEmail = ref('');

/**
 * Validations on client side
 */
const validateForm = () => {
  errors.value = {};
  let isValid = true;

  // Empty input validation
  if (!email.value || email.value.trim() === '') {
    errors.value.email = t('auth.forgotPassword.validation.emailRequired');
    isValid = false;
  }
  // Email format validation
  else if (!isValidEmail(email.value)) {
    errors.value.email = t('auth.forgotPassword.validation.emailInvalid');
    isValid = false;
  }

  return isValid;
};

/**
 * Check email format
 */
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Send email to reset password
 */
const submit = async () => {
  // Validations on client side
  if (!validateForm()) {
    return;
  }

  loading.value = true;
  errors.value = {};

  try {
    await forgotPassword(email.value);
    submittedEmail.value = email.value;
    submitted.value = true;
    email.value = '';
  } catch (error) {
    // For security reasons, treat user not found (422) as success
    // to avoid revealing whether an email exists in the system
    if (error.status === 422 && error.message === 'passwords.user') {
      submittedEmail.value = email.value;
      submitted.value = true;
      email.value = '';
    }
    // Handle validation errors (invalid email format, etc.)
    else if (error.status === 422) {
      if (error.errors) {
        errors.value = error.errors;
      } else {
        errors.value = { email: error.message };
      }
    }
    // Handle other errors
    else {
      errorMessage(t('auth.forgotPassword.failed'));
    }
  } finally {
    loading.value = false;
  }
};

/**
 * Navigate to login page
 */
const goToLogin = () => {
  router.push({ name: 'login' });
};
</script>

<style lang="scss" scoped>
.forgot-password-card {
  background-color: v-bind('colors.white');
  padding: 2.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px v-bind('colors.shadowColor');
}

.page-title {
  color: v-bind('colors.textPrimary');
  font-size: 1.5rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 1.5rem;
}

.description {
  color: v-bind('colors.textPrimary');
  font-size: 14px;
  line-height: 1.6;
  text-align: center;
  margin-bottom: 2rem;

  strong {
    font-weight: 600;
  }
}

.form-group {
  margin-bottom: 1.5rem;

  .form-label {
    color: v-bind('colors.textPrimary');
    font-weight: 500;
    margin-bottom: 0.5rem;
    display: block;
  }

  .form-control {
    width: 100%;
    border: 1px solid v-bind('colors.borderMediumGray');
    border-radius: 4px;
    padding: 0.75rem;
    color: v-bind('colors.textPrimary');

    &:focus {
      border-color: v-bind('colors.btnBgColorPrimary');
      box-shadow: 0 0 0 0.2rem v-bind('colors.shadowPrimary');
      outline: none;
    }

    &.is-invalid {
      border-color: v-bind('colors.error');
    }
  }

  .invalid-feedback {
    color: v-bind('colors.error');
    font-size: 0.875rem;
    margin-top: 0.25rem;
    display: block;
  }
}

.btn {
  width: 100%;
  padding: 0.75rem;
  font-weight: 500;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.btn-submit {
  background-color: v-bind('colors.btnBgColorPrimary');
  border-color: v-bind('colors.btnBgColorPrimary');
  color: v-bind('colors.white');
  margin-bottom: 1rem;
  border: none;

  &:hover:not(:disabled) {
    background-color: v-bind('colors.blue[600]');
    border-color: v-bind('colors.blue[600]');
  }

  &:active:not(:disabled) {
    background-color: v-bind('colors.blue[700]');
  }
}

.btn-back {
  background-color: v-bind('colors.white');
  color: v-bind('colors.btnBgColorPrimary');
  border: 1px solid v-bind('colors.borderMediumGray');

  &:hover:not(:disabled) {
    background-color: v-bind('colors.gray[50]');
  }

  &:active:not(:disabled) {
    background-color: v-bind('colors.gray[300]');
  }
}
</style>
